/**
 * <s-bundle-picker> — "1 brush / 2 brushes" cards above the add-to-cart button.
 *
 * Choosing a card sets the product form's quantity input and swaps the
 * add-to-cart label for that bundle's total. Changing the quantity by hand
 * selects the matching card, or clears both when no card matches.
 *
 * Markup:
 *   input[type="radio"][data-bundle-quantity] — one per card; value is the quantity.
 *   template[data-bundle-label="<qty>"]       — add-to-cart label for that quantity.
 *
 * Attributes:
 *   data-quantity-input — selector for the form's quantity input.
 *   data-button         — selector for the add-to-cart button.
 */
class SBundlePicker extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('change', this.#onPick, { signal });
    this.#quantityInput()?.addEventListener('change', this.#onQuantity, { signal });

    const checked = this.querySelector('[data-bundle-quantity]:checked');
    if (checked) this.#apply(Number(checked.value), false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #quantityInput() {
    return document.querySelector(this.dataset.quantityInput || '#product-quantity');
  }

  #button() {
    return document.querySelector(this.dataset.button || '#product-buy [name="add"]');
  }

  #onPick = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || !radio.matches('[data-bundle-quantity]')) return;
    this.#apply(Number(radio.value), true);
  };

  #onQuantity = () => {
    const value = Number.parseInt(this.#quantityInput()?.value || '1', 10);
    let matched = false;
    for (const radio of this.querySelectorAll('[data-bundle-quantity]')) {
      radio.checked = Number(radio.value) === value;
      matched ||= radio.checked;
    }
    this.#setLabel(matched ? value : null, value);
  };

  #apply(quantity, updateInput) {
    const input = this.#quantityInput();
    if (input && updateInput && Number(input.value) !== quantity) {
      input.value = String(quantity);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return; // The change handler sets the label.
    }
    this.#setLabel(quantity, quantity);
  }

  #setLabel(bundleQuantity, quantity) {
    const button = this.#button();
    if (!button) return;
    // Quantities without a card fall back to the single-unit label.
    const template =
      (bundleQuantity && this.querySelector(`template[data-bundle-label="${bundleQuantity}"]`)) ||
      this.querySelector('template[data-bundle-label="1"]');
    if (template) button.innerHTML = template.innerHTML.trim();
    button.dataset.quantity = String(quantity);
  }
}

if (!customElements.get('s-bundle-picker')) {
  customElements.define('s-bundle-picker', SBundlePicker);
}
